import { toArray, groupBy } from '@nx-superprep/utils'

import { Choice, ChoiceType } from './choice.model'
import { Answer } from './answer.model'
import { Question } from './question.model'
import { ResourceGroup } from './resource.model'
import { cardTemplateChoices } from './card-template-utils'
import { Gameplay } from './gameplay-info.model'

import { CardTemplate, ConceptInfo } from '../../back-end'
/**
 *  card template web app model
 */
export class FrontendCardTemplate extends CardTemplate {
  // shorthands
  cardID = ''
  moduleID = ''
  //
  subModule = ''
  element = ''
  sibling = ''
  language = ''
  supplement = 0

  // expanded structures for card play

  // new gameplay structure
  gameplay!: Gameplay
  // new concept structure
  concept!: ConceptInfo
  //
  shouldSkipReview = false
  //
  assets: string[] = []
  //
  audios: Choice[] = []
  choices: Choice[] = []
  question: Question | undefined
  answer: Answer | undefined
  choiceSet: {visible: number; possible: number; answer: number} = {visible:0, possible:0, answer:0}
  private _allChoices: (Choice|Answer|Question)[] = []

  static getAssetList(card: FrontendCardTemplate | FrontendCardTemplate[]): ResourceGroup[] {
    const resources = toArray(card)
      .flatMap((c) => c.allChoices)
      .flatMap((c) => c.resources)
      .map((r) => new ResourceGroup(r))
    const groups = groupBy(resources, 'assetType') as Record<string, ResourceGroup[]>
    return Object.values(groups).flat()
  }

  static isValidCard({
    gameplay,
    question,
    choices,
  }: {
    gameplay?: Gameplay
    question?: Question
    choices?: Choice[]
  }) {
    let valid = question?.isValid
    if (!valid) {
      // log.warn('invalid question', gameplay, question, choices);
      return false
    }
    const answer = choices?.find((c) => c.isAnswer)
    valid = answer?.isValid
    if (!valid) {
      // log.warn('invalid answer', gameplay, question, choices);
      return false
    }
    const count = choices?.filter((c) => c.isValid).length ?? 0
    valid = gameplay?.type === 'diagram' || (gameplay?.choiceCount ?? 0) <= count
    if (!valid) {
      // log.warn('invalid choices', gameplay, question, choices);
      return false
    }
    return true
  }

  constructor(options: CardTemplate) {
    if (
      !options?.id
      || !options.moduleId
      || !options.submoduleId
      || !options.elementId
      || !options.siblingId
      || !options.cardQuestion
      || !options.cardAnswer
      || !options.gameplayInfo
      || !options.conceptInfo
      || !options.spacedRepInfo
    ) { throw new Error(`Invalid card template ${options.id} ${JSON.stringify(options)}`) }

    super(options)

    // renaming some fields for compatibility with old card template
    this.cardID = options.id
    this.moduleID = options.moduleId ?? ''
    this.subModule = options.submoduleId ?? ''
    this.subModule = options.submoduleId
    this.element = options.elementId
    this.sibling = options.siblingId
    // expose card name fields as properties
    const cardFields = options.id.split('-')
    this.language = cardFields[4]
    this.supplement = Number(cardFields[6])||0
    // gameplay structure
    this.gameplay = new Gameplay(options.gameplayInfo ?? {})
    // concept structure
    this.concept = options.conceptInfo

    this.shouldSkipReview = Number(this.supplement) === 1

    this.choiceSet.visible = this.gameplayInfo.choiceCount

    this.allChoices = (cardTemplateChoices(options) ?? []).map((c) => {
      switch (c.type) {
        case ChoiceType.question: {
          this.question = new Question(c as Question)
          return this.question
        }
        case ChoiceType.answer: {
          this.answer = new Answer(c as Answer)
          this.choiceSet.answer = this.answer?.tuple.length ?? 0
          return this.answer
        }
        default: {
          const choice = new Choice(c as Choice)
          this.choices.push(choice)
          this.choiceSet.possible++
          return choice
        }
      }
    })
  }

  /**
   * Return true if CardTemplate is a sibling of card
   *
   * @param card the card to compare to
   */
  isSiblingOf(card: FrontendCardTemplate): boolean {
    return (
      card &&
      this.subModule === card.subModule &&
      this.element === card.element
    )
  }
  /**
   * Return true if CardTemplate is a sibling equal or greater than card
   *
   * @param card the card to compare to
   */
  isSiblingEqualOrGreater(card: FrontendCardTemplate) {
    return card && this.isSiblingOf(card) && Number(this.sibling) >= Number(card.sibling)
  }
  /**
   * Return true if CardTemplate is the same sibling as card
   *
   * @param card the card to compare to
   */
  isSiblingEqual(card: FrontendCardTemplate) {
    return card && this.isSiblingOf(card) && Number(this.sibling) === Number(card.sibling)
  }
  /**
   * Return true if CardTemplate has the same gameplay as card
   *
   * @param card the card to compare to
   */
  isSameGameplay(card: FrontendCardTemplate) {
    return card && this.gameplay?.type === card.gameplay?.type
  }

  updateAudioDuration(isCorrect: boolean) {
    if (
      this.answer?.hasAudio &&
      this.answer.audio?.name === this.question?.audio?.name
    ) {
      this.answer.duration = this.question?.duration ?? 0
    }
  }

  // choices

  getChoice(isAnswer = false) {
    return this.choices.find(c => isAnswer ? c.isAnswer : !c.isAnswer)
  }

  get allChoices() {
    return this._allChoices;
  }
  set allChoices(value) {
    this._allChoices = value
    this.choices = value.filter((c) => c.isChoice)
    this.answer = value.find((c) => c.isAnswer) as Answer
    this.question = value.find((c) => c.isQuestion) as Question
    this.audios = this.question?.hasAudio ? [this.question] : this.choices.filter((c) => c.isAudio)
  }

  get resources() {
    return this.allChoices.flatMap((c) => c.resources)
  }

  /** True if all card components have their assets */
  get isAssetized() {
    return this.allChoices.length > 0 && this.allChoices.every((c) => c.isAssetized);
  }
  /** True if card has all its components */
  get isValid() {
    return FrontendCardTemplate.isValidCard({
      gameplay: this.gameplay,
      question: this.question,
      choices: this.choices,
    })
  }

  get isAudioAnswer() {
    return this.answer?.hasAudio
  }

  get isAudioQuestion() {
    return (this.question?.isAudio || this.audios.length > 1)
  }

  get isAudioOnlyQuestion() {
    return this.isAudioQuestion && !(this.question?.hasText || this.question?.hasElement)
  }

  get isAudioOnlyAnswer() {
    return this.answer?.hasAudio && !(this.answer.hasText || this.answer.hasElement)
  }

  get isImageOnlyQuestion() {
    return this.question?.isImage
  }

  get isImageOnlyAnswer() {
    return !this.answer?.hasText || (this.answer.hasImage && this.answer.isText)
  }

  get isVideoOnlyQuestion() {
    return this.question?.isVideo
  }

  get isVideoOnlyAnswer() {
    return this.answer?.hasVideo && !this.answer.hasText
  }

  get isAudioOnly() {
    return this.isAudioOnlyQuestion && this.isAudioOnlyAnswer
  }

  get isImageOnly() {
    return this.isImageOnlyQuestion && this.isImageOnlyAnswer
  }

  get isVideoOnly() {
    return this.isVideoOnlyQuestion && this.isVideoOnlyAnswer
  }

  get isMediaOnly() {
    return this.isAudioOnly || this.isImageOnly || this.isVideoOnly
  }
}
